import { useCallback, useEffect, useMemo, useState } from 'react'
import { businessEventsApi, type BusinessEventRow, type BusinessEventTypeInfo } from '../api/businessEvents'
import {
  Page,
  PageHeader,
  FilterBar,
  Select,
  TextField,
  AccessDenied,
  Callout,
  LoadingState,
  EmptyState,
  Card,
  DataTable,
  StatusPill,
  Pagination,
  KeyValueList,
} from '../components/ui'

/**
 * F-086 (TASK-0340, REQ-297-001, REQ-030-001): registro degli eventi di business (solo Admin). Ogni evento che la
 * piattaforma annuncia (pratiche, lead, appuntamenti, eccezioni, SLA, job, approvazioni, regole, documenti) lascia qui
 * una riga, anche se nessuna automazione e nessun webhook lo ascolta. Le righe "baseline" sono fatti gia' veri al primo
 * giro del rilevatore: registrati, mai annunciati. Solo identificativi e vocabolario: nessun importo, nessun dato personale.
 */
const PAGE_SIZE = 50

const ENTITY_LABELS: Record<string, string> = {
  Contract: 'Pratica',
  Lead: 'Lead',
  Appointment: 'Appuntamento',
  Configuration: 'Configurazione',
  Job: 'Job',
  Document: 'Documento',
  Approval: 'Approvazione',
}

const ATTRIBUTE_LABELS: Record<string, string> = {
  status: 'Stato',
  fromStatus: 'Stato precedente',
  exceptionKind: 'Eccezione',
  ruleCode: 'Regola',
  configurationKind: 'Configurazione',
  changeKind: 'Modifica',
  jobType: 'Job',
  attempts: 'Tentativi',
  contractId: 'Pratica',
  documentKind: 'Documento',
  templateCode: 'Modello',
  templateVersion: 'Versione',
  approvalKind: 'Approvazione',
  subjectId: 'Id oggetto',
  decision: 'Esito',
  agentId: 'Agente',
}

function formatDateTime(s: string): string {
  return new Date(s).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

function shortValue(key: string, value: string): string {
  // Il tipo di job e' un nome CLR completo: basta l'ultimo pezzo.
  if (key === 'jobType') {
    const i = value.lastIndexOf('.')
    return i >= 0 ? value.slice(i + 1) : value
  }
  return value
}

function summary(attributes: Record<string, string>): string {
  return Object.entries(attributes)
    .filter(([k]) => k in ATTRIBUTE_LABELS)
    .slice(0, 4)
    .map(([k, v]) => `${ATTRIBUTE_LABELS[k]}: ${shortValue(k, v)}`)
    .join(' · ')
}

function isForbidden(e: unknown): boolean {
  const msg = e instanceof Error ? e.message : ''
  return msg.includes('403') || msg.toLowerCase().includes('forbidden') || msg.toLowerCase().includes('autorizzat')
}

export function BusinessEventsPage() {
  const [types, setTypes] = useState<BusinessEventTypeInfo[]>([])
  const [type, setType] = useState('')
  const [entityType, setEntityType] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [origin, setOrigin] = useState<'all' | 'announced' | 'baseline'>('all')
  const [page, setPage] = useState(1)

  const [items, setItems] = useState<BusinessEventRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [unauthorized, setUnauthorized] = useState(false)
  const [open, setOpen] = useState<number | null>(null)

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const labelOf = useMemo(() => new Map(types.map(t => [t.code, t.label])), [types])
  const entityTypes = useMemo(() => [...new Set(types.map(t => t.entityType))], [types])

  useEffect(() => {
    businessEventsApi.types().then(setTypes).catch(e => {
      if (isForbidden(e)) setUnauthorized(true)
    })
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await businessEventsApi.list({
        type: type || undefined,
        entityType: entityType || undefined,
        from: from || undefined,
        to: to || undefined,
        published: origin === 'all' ? undefined : origin === 'announced',
        page,
        pageSize: PAGE_SIZE,
      })
      setItems(res.items)
      setTotal(res.total)
    } catch (e) {
      if (isForbidden(e)) setUnauthorized(true)
      else setError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setLoading(false)
    }
  }, [type, entityType, from, to, origin, page])

  useEffect(() => {
    void load()
  }, [load])

  function resetPage<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v)
      setPage(1)
    }
  }

  return (
    <Page>
      <PageHeader
        title="Registro eventi"
        icon="event_note"
        count={!unauthorized ? total : undefined}
      />

      {unauthorized && <AccessDenied requirement="il ruolo Admin" />}
      {!unauthorized && error && <Callout tone="error">{error}</Callout>}

      {!unauthorized && (
        <>
          <FilterBar>
            <Select label="Evento" id="be-type" value={type}
              onChange={e => resetPage(setType)(e.target.value)}>
              <option value="">Tutti</option>
              {types.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
            </Select>
            <Select label="Riguarda" id="be-entity" value={entityType}
              onChange={e => resetPage(setEntityType)(e.target.value)}>
              <option value="">Tutto</option>
              {entityTypes.map(t => <option key={t} value={t}>{ENTITY_LABELS[t] ?? t}</option>)}
            </Select>
            <Select label="Origine" id="be-origin" value={origin}
              onChange={e => resetPage(setOrigin)(e.target.value as 'all' | 'announced' | 'baseline')}>
              <option value="all">Tutti</option>
              <option value="announced">Annunciati</option>
              <option value="baseline">Baseline (non annunciati)</option>
            </Select>
            <TextField label="Dal" id="be-from" type="date" value={from}
              onChange={e => resetPage(setFrom)(e.target.value)} />
            <TextField label="Al" id="be-to" type="date" value={to}
              onChange={e => resetPage(setTo)(e.target.value)} />
          </FilterBar>

          <Card variant="outlined" padding="none">
            {loading ? (
              <LoadingState />
            ) : items.length === 0 ? (
              <EmptyState icon="event_busy" title="Nessun evento con questi filtri" />
            ) : (
              <DataTable variant="plain">
                <thead>
                  <tr>
                    <th>Quando</th>
                    <th>Evento</th>
                    <th>Riguarda</th>
                    <th>Dettagli</th>
                    <th className="text-end">Origine</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(row => (
                    <FragmentRow key={row.id} row={row} label={labelOf.get(row.eventType) ?? row.eventType}
                      expanded={open === row.id} onToggle={() => setOpen(open === row.id ? null : row.id)} />
                  ))}
                </tbody>
              </DataTable>
            )}
            {totalPages > 1 && <Pagination page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} />}
          </Card>
        </>
      )}
    </Page>
  )
}

function FragmentRow(props: { row: BusinessEventRow; label: string; expanded: boolean; onToggle: () => void }) {
  const { row, label, expanded, onToggle } = props
  return (
    <>
      <tr role="button" onClick={onToggle}>
        <td className="text-muted md-typescale-body-small text-nowrap">{formatDateTime(row.occurredAt)}</td>
        <td className="md-typescale-emphasized-body-medium">
          {label}
          <div className="text-muted md-typescale-body-small">{row.eventType}</div>
        </td>
        <td className="md-typescale-body-small text-nowrap">{ENTITY_LABELS[row.entityType] ?? row.entityType} #{row.entityId}</td>
        <td className="text-muted md-typescale-body-small">{summary(row.attributes) || '—'}</td>
        <td className="text-end">
          {row.published
            ? <StatusPill tone="success">Annunciato</StatusPill>
            : <StatusPill tone="neutral" title="Fatto gia' vero al primo giro del rilevatore: registrato, non annunciato">Baseline</StatusPill>}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={5} className="md-typescale-body-small md-u-surface-low">
            <div className="px-3 py-2">
              <KeyValueList
                items={[
                  { label: 'Chiave', value: <span className="text-break">{row.eventKey}</span> },
                  { label: 'Registrato il', value: formatDateTime(row.recordedAt) },
                  {
                    label: 'Attributi',
                    value: Object.keys(row.attributes).length === 0
                      ? '—'
                      : Object.entries(row.attributes).map(([k, v]) => (
                        <div key={k}><code>{k}</code>: {v}</div>
                      )),
                  },
                ]}
              />
            </div>
          </td>
        </tr>
      )}
    </>
  )
}
